import { Dispatch, SetStateAction } from 'react';
import { replaceUrlParams } from '@catalogo/service-americanas-product/src/helpers/url';
import { IService } from '../types/global';
import { IOfferLine, IGetBasketUrlParams, IGetNextUrlParams } from '../types/add-to-cart';
import {
  IProceedToCartFlowProps,
  IHandleAddToCartProps,
  ISetActionsEffect,
  IAddToCartEffectProps,
  IHandleAddToCartInModal,
} from '../types/add-to-cart-pdp';

export const MODAL_WARRANTY_URL_REF = '#modal-warranty-pdp';

/**
 * Retorna a url da cesta.
 * @param {string} brand Marca (acom, shop, suba).
 * @param {string} cartId Id do cart.
 * @param {string | undefined} sellerStoreId Id da Warehouse/Loja.
 * @param {string | undefined} experiment resultado do teste A/B.
 * @return {string}
 */

export const getBasketUrl = ({ brand, cartId, sellerStoreId, experiment, forceNewUrl }: IGetBasketUrlParams) => {
  const queryParams = `?cartId=${cartId}${sellerStoreId ? `&sellerStoreId=${sellerStoreId}` : ''}`;
  const url = `https://sacola.${brand}.com.br/carrinho${queryParams}`;
  const newUrl = `https://sacola.americanas.com.br/compra/cesta/${queryParams}`;

  if (forceNewUrl) {
    return newUrl;
  }

  return experiment?.toUpperCase() !== 'NEW' ? url : newUrl;
};

/**
 * Define a próxima url para do fluxo do buy button
 * @param {boolean} isAcom
 * @param {boolean} hasServices
 * @param {boolean} hasServiceToAddWithProduct
 * @param {boolean} showModal
 * @param {string | undefined} basketUrl Url do cart.
 * @param {string | undefined} url url default do fluxo (/garantia, #modal ...).
 * @param {boolean} isMobile tipo de dispositivo.
 * @param {string | undefined} modalWarrantyExperiment resultado do teste A/B.
 * @param {string | undefined} cartExperiment resultado do teste A/B.
 *
 * @return {string}
 */
export const getNextUrl = ({
  isAcom,
  hasServices,
  hasServiceToAddWithProduct,
  showModal,
  basketUrl,
  url,
  isMobile,
  modalWarrantyExperiment,
  cartExperiment,
}: IGetNextUrlParams) => {
  let nextUrl = isAcom && (!hasServices || hasServiceToAddWithProduct) && !showModal && basketUrl ? basketUrl : url;

  if (isAcom && !isMobile && nextUrl?.includes('/garantia')) {
    nextUrl = basketUrl;
  } else if (isAcom && isMobile && modalWarrantyExperiment?.toUpperCase() !== 'NEW' && nextUrl?.includes('/garantia')) {
    nextUrl = `${nextUrl}&cartExperiment=${cartExperiment}`;
  }

  return nextUrl;
};

/**
 * Segue o fluxo para o cart: abrindo o modal ou fazendo um redirect para a cesta.
 * @return {void}
 */
export const proceedToCartFlow = ({
  showModal,
  history,
  nextUrl,
  hasServices,
  servicePDPinfoForWarranty,
  buyTogetherVIP,
  currentSku,
  cartExperiment,
}: IProceedToCartFlowProps) => {
  if (showModal && history) {
    replaceUrlParams(history, nextUrl, false, {
      hasServices,
      servicePDPinfoForWarranty,
      buyTogetherVIP,
      currentSku,
      cartExperiment,
    })();
  } else {
    window.location.href = nextUrl;
  }
};

/**
 * Inicia o fluxo de adicionar o(s) produto(s) no cart.
 * @return {void}
 */
export const handleAddToCart = ({
  nextUrl,
  offers,
  servicestoAdd,
  setActions,
  hasServices,
  history,
  showModal,
  servicePDPinfoForWarranty,
  buyTogetherVIP,
  currentSku,
  cartExperiment,
}: IHandleAddToCartProps) => () => {
  const { offersToAdd, offersToUpdate } = offers;
  const hasOffersToAdd = (offersToAdd as Array<IOfferLine>)?.length > 0;
  const hasOffersToUpdate = (offersToUpdate as Array<IOfferLine>)?.length > 0;
  const hasServicestoAdd = servicestoAdd?.length > 0;
  const hasActionToAddOrUpdate = hasOffersToAdd || hasOffersToUpdate || hasServicestoAdd;
  const showModalWarranty = showModal && nextUrl?.includes(MODAL_WARRANTY_URL_REF) && hasActionToAddOrUpdate;

  if (showModalWarranty || (!showModal && hasActionToAddOrUpdate)) {
    // Define as acoes de que serao executadas pelo graphQL
    setActions({ add: hasOffersToAdd, update: hasOffersToUpdate, addService: hasServicestoAdd });
  }

  if ((showModal && !showModalWarranty) || (!showModal && !hasActionToAddOrUpdate)) {
    // Joga o fluxo de adicionar pelo addToCart para o modal OU direciona para o cart quando nao ha acoes a serem executadas
    proceedToCartFlow({
      showModal,
      history,
      nextUrl,
      hasServices,
      servicePDPinfoForWarranty,
      buyTogetherVIP,
      currentSku,
      cartExperiment,
    });
  }
};

/**
 * Efeito colateral disparado apos produto ser adicionado/atualizado no carrinho pelo graphQl
 * @return {void}
 */
export const addToCartEffect = ({
  showModal,
  history,
  nextUrl,
  cartId,
  addToCartError,
  errorAddService,
  isAddedToCart,
  isUpdatedToCart,
  isServiceAdded,
  hasServices,
  servicePDPinfoForWarranty,
  currentSku,
  cartExperiment,
}: IAddToCartEffectProps) => () => {
  /*
    O isAddedToCart, isUpdatedToCart e isServiceAdded true em conjunto com o estado de erro do graphQL false
    indicam que o processo foi disparado e finalizado pela mutation
  */
  if (isAddedToCart && isUpdatedToCart && isServiceAdded && cartId && !addToCartError && !errorAddService) {
    proceedToCartFlow({
      showModal,
      history,
      nextUrl,
      hasServices,
      servicePDPinfoForWarranty,
      currentSku,
      cartExperiment,
    });
  }
};

/**
 * Efeito colateral disparado apos acoes a serem executadas pelo graphQL serem definidas
 * @return {void}
 */
export const setActionsEffect = ({
  actions,
  cartId,
  cartLines,
  addToCart,
  addServiceToCartFn,
  offersToAdd,
  offersToUpdate,
  servicestoAdd,
  isAddedToCart,
  isUpdatedToCart,
  isServiceAdded,
}: ISetActionsEffect) => () => {
  const { add, update, addService } = actions;
  if (add && !isAddedToCart) {
    // Adiciona o(s) produto(s) no cart pelo graphQL
    addToCart({ offersToAdd });
  } else if (update && !isUpdatedToCart) {
    // Atualiza o(s) produto(s) no cart pelo graphQL
    addToCart({ offersToUpdate });
  } else if (addService && cartId && !isServiceAdded) {
    const lineId = cartLines?.find(line => line?.product?.sku === servicestoAdd?.[0]?.productSku)?.id;
    addServiceToCartFn(cartId, lineId as string, servicestoAdd);
  }
};

/**
 * Recupera os serviços que serão adicionados pela mutation de serviços
 * @param {boolean} hasServiceToAddWithProduct
 * @param {IService | undefined} servicePDPinfoForWarranty
 * @return {Array<IService>}
 */
export const getServicesToAdd = (hasServiceToAddWithProduct: boolean, servicePDPinfoForWarranty?: IService) => {
  // Regra: o servico esta selecionado na PDP e nao sera adicionado com o produto
  if (servicePDPinfoForWarranty && !hasServiceToAddWithProduct) {
    return [servicePDPinfoForWarranty];
  }

  return [];
};

/**
 * Ação disparada no onMutationComplete de services
 * @param {Dispatch<SetStateAction<boolean>>} setIsServiceAdded
 * @return {void}
 */
export const onServiceIsAdded = (setIsServiceAdded: Dispatch<SetStateAction<boolean>>) => () => setIsServiceAdded(true);

/**
 * Inicia o fluxo de adicionar o(s) produto(s) no cart em modais que abrem modal de garantia.
 * @return {void}
 */
export const handleAddToCartInModal = ({
  history,
  url,
  warrantyUrl,
  handleAddProduct,
}: IHandleAddToCartInModal) => () => {
  if (warrantyUrl?.includes(MODAL_WARRANTY_URL_REF)) {
    replaceUrlParams(history, url)();
    setTimeout(() => handleAddProduct(), 200);
  } else {
    handleAddProduct();
  }
};
